/**
 * Le code dell'area segreteria, sul flusso nuovo (24-10-2026, come Lares).
 *
 * Fino a quel giorno l'area leggeva `GET /api/bookings/pending-secretary` e assegnava con
 * `POST /api/bookings/{id}/secretary-assign`: il modello vecchio, che non vede le richieste nuove.
 * Una richiesta d'auto approvata dal responsabile (`pm-approve` scrive solo lo stato nuovo) non
 * arrivava quindi mai in segreteria da qui. Ora:
 *
 * | Scheda | Legge | Si lavora in |
 * |---|---|---|
 * | Auto da assegnare | `vehicle-bookings/pending-secretary` | `FleetBookingDetailDrawer` (assegna, cambia mezzo, sollecita, annulla) |
 * | Trasferte da preparare | `travel-bookings/pending-secretary` | `TravelDetailDrawer` (preventivi, conferma) |
 * | Annullamenti | gli annullati con qualcosa da disfare | `CancellationsToHandle` |
 * | Danni e anomalie | `vehicle-incidents` | chiusura qui |
 *
 * Le due code tornano righe piatte, senza il dipendente: si prendono gli id e si rileggono come
 * risorse, come fa Lares.
 */
import { fetchAllPages } from '@/shared/api/jsonapi/client';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { hasClaim, CLAIM } from '@/shared/permissions/claims';
import { fetchTravelPendingSecretary, type TravelBookingRow } from '@/features/travel/travelApi';

/** Rilegge una coda come risorse, a blocchi: cento id in un filtro sono un indirizzo troppo lungo. */
export async function rowsByIds<T extends Denormalized>(type: string, ids: string[], include: string[], sort: string[]): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += 50) {
    out.push(...await fetchAllPages<T>(type, { filter: { id: { in: ids.slice(i, i + 50) } }, include, sort }));
  }
  return out;
}

export async function fetchTravelSecretaryRows(): Promise<TravelBookingRow[]> {
  const ids = (await fetchTravelPendingSecretary()).map((r) => r.id).filter(Boolean);
  return rowsByIds<TravelBookingRow>('travel-bookings', ids, ['employee'], ['fromDate']);
}

export function personLabel(p?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null): string {
  return p ? ([p.lastName, p.firstName].filter(Boolean).join(' ') || p.email || '—') : '—';
}

export type SecretaryTab = 'cars' | 'travel' | 'cancellations' | 'defects' | 'fleet' | 'rooms';

/**
 * Chi configura le sale riunioni. Dal 02-11-2026 la scheda sta qui, e prima era in «Segreteria & HR»:
 * la vede chi la vedeva là (l'anagrafica di tutta l'azienda), chi scrive le sale e chi ne conferma
 * le prenotazioni. Va tenuta uguale alla voce di menu dell'area (`appNav.ts`).
 */
export const ROOMS_ADMIN_CLAIMS = [
  'MeetingRoom.update', 'meetingRoom.booking.approve.write', 'UserProfile.readAll', 'ErpEmployee.update',
] as const;

export interface SecretaryAccess {
  claims: readonly string[];
  isSuperAdmin: boolean;
  flagOn: (flag: string, defaultValue: boolean) => boolean;
}

/**
 * Le schede che questa persona può usare, nell'ordine in cui si mostrano. Ogni scheda vuole il suo
 * modulo **e il suo permesso** (02-11-2026: prima annullamenti, danni e mezzi si vedevano a chiunque
 * aprisse l'area col modulo acceso, e i comandi rispondevano 403):
 *
 *  · le due code e gli annullamenti di un modulo — il permesso di assegnare di quel modulo, lo
 *    stesso che il sistema chiede per chiudere una pratica di annullamento;
 *  · danni e anomalie — `VehicleIncident.update`, che serve a chiuderle;
 *  · gestione mezzi — `Vehicle.update`;
 *  · sale — `ROOMS_ADMIN_CLAIMS`.
 */
/**
 * Di quali moduli si vedono gli annullamenti: di quelli accesi in cui si può assegnare. Chi assegna
 * solo le auto non vede gli annullamenti delle trasferte, che non potrebbe chiudere.
 */
export function cancellationModules(a: SecretaryAccess): { fleet: boolean; travel: boolean } {
  const can = (claim: string) => a.isSuperAdmin || hasClaim(a.claims, claim);
  return {
    fleet: a.flagOn('fleet.enabled', true) && can(CLAIM.fleetBookingAssign),
    travel: a.flagOn('travel.enabled', true) && can(CLAIM.travelBookingAssign),
  };
}

export function secretaryTabs(a: SecretaryAccess): SecretaryTab[] {
  const fleet = a.flagOn('fleet.enabled', true);
  const travel = a.flagOn('travel.enabled', true);
  const can = (claim: string) => a.isSuperAdmin || hasClaim(a.claims, claim);
  const out: SecretaryTab[] = [];
  const { fleet: fleetCancellations, travel: travelCancellations } = cancellationModules(a);
  if (fleet && can(CLAIM.fleetBookingAssign)) out.push('cars');
  if (travel && can(CLAIM.travelBookingAssign)) out.push('travel');
  if (fleetCancellations || travelCancellations) out.push('cancellations');
  if (fleet && can('VehicleIncident.update')) out.push('defects');
  if (fleet && can('Vehicle.update')) out.push('fleet');
  if (a.flagOn('meetingRoom.enabled', true) && ROOMS_ADMIN_CLAIMS.some(can)) out.push('rooms');
  return out;
}

export const SECRETARY_TAB_LABEL: Record<SecretaryTab, string> = {
  cars: 'Auto da assegnare',
  travel: 'Trasferte da preparare',
  cancellations: 'Annullamenti',
  defects: 'Danni e anomalie',
  fleet: 'Gestione mezzi',
  rooms: 'Sale',
};
